/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Comments tab for the Expense Inventory detail view. Mirrors the
 * "immediate save" pattern used by InventoryRequestDetail/Comments.tsx:
 * comments are persisted (added/edited/deleted) right away via the generic
 * createComment/editComment/deleteComment mutations, rather than staged and
 * submitted alongside a larger mutation. Comments remain addable regardless
 * of whether the rest of the case is editable, since they're a parallel
 * communication channel rather than a case-data edit control.
 *
 * @module src/components/Inventory/expense/detail/ExpenseInventoryComments
 */
import React, { FC, useState } from 'react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { useSelector } from 'react-redux';
import Card from '@salesforce/design-system-react/components/card';
import Icon from '@salesforce/design-system-react/components/icon';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import Button from '@salesforce/design-system-react/components/button';
import Spinner from '@salesforce/design-system-react/components/spinner';
import Modal from '@salesforce/design-system-react/components/modal';
import { Grid } from '@material-ui/core';
import moment from 'moment';
import TextArea from '../../../../Shared/TextArea';
import SnackBar from '../../../../Shared/SnackBar';
import useSnackBar from '../../../../../util/customHooks/useSnackBar';
import { NOTIFY_TYPE } from '../../../../../util/constants';
import { getUTCDateTime } from '../../../../../util/utilityFunctions';
import { getUserInfo } from '../../../../../store/ducks/userInfo';
import { GET_COMMENT_HISTORY } from '../../../../../graphql/getCommentHistory';
import CREATE_COMMENT from '../../../../../graphql/mutations/createComment';
import EDIT_COMMENT from '../../../../../graphql/mutations/editComment';
import DELETE_COMMENT from '../../../../../graphql/mutations/deleteComment';

const SCREEN_NAME = 'Expense Inventory';
const COMMENT_TYPE = 'Customer';

interface Props {
  caseExternalId?: string;
}

interface Comment {
  comment: string;
  postTime: string;
  userName?: string;
  userId?: any;
  commentExternalId?: any;
  updatedBy?: string;
}

const ExpenseInventoryComments: FC<Props> = ({ caseExternalId }) => {
  const { open, notification, openSnackBar } = useSnackBar();
  const userInfo = useSelector(getUserInfo);
  const [input, setInput] = useState('');
  const [isEdit, setIsEdit] = useState('');
  const [editFieldText, setEditFieldText] = useState('');
  const [deleteExternalId, setDeleteExternalId] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [loader, setLoader] = useState(false);

  const { data: commentHistory, refetch: refetchCommentHistory } = useQuery(GET_COMMENT_HISTORY, {
    variables: { caseExternalId, commentType: COMMENT_TYPE },
    skip: !caseExternalId,
  });
  const commentsList: Comment[] = commentHistory?.getCommentHistory ?? [];

  const [createComment] = useMutation(CREATE_COMMENT);
  const [editComment, { loading: editCommentLoading }] = useMutation(EDIT_COMMENT);
  const [deleteComment, { loading: deleteCommentLoading }] = useMutation(DELETE_COMMENT);

  const handleAdd = (): void => {
    if (!input) {
      openSnackBar(NOTIFY_TYPE.INFO, 'Please add your comment first');
      return;
    }
    setLoader(true);
    createComment({
      variables: {
        comment: input,
        caseExternalId,
        postTime: getUTCDateTime(new Date()),
        commentType: COMMENT_TYPE,
        screen: SCREEN_NAME,
      },
    })
      .then(() => {
        setInput('');
        return refetchCommentHistory();
      })
      .catch(() => {
        openSnackBar(NOTIFY_TYPE.ERROR, 'Something went wrong. Please try again.');
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const handleEdit = (comment: Comment): void => {
    if (!editFieldText) {
      openSnackBar(NOTIFY_TYPE.INFO, 'Please add your comment first');
      return;
    }
    editComment({
      variables: {
        externalId: isEdit,
        comment: editFieldText,
        postTime: getUTCDateTime(new Date()),
      },
    })
      .then(() => refetchCommentHistory())
      .catch(() => {
        openSnackBar(NOTIFY_TYPE.ERROR, 'Something went wrong. Please try again.');
      });
    setIsEdit('');
  };

  const handleDelete = (): void => {
    if (!deleteExternalId) {
      openSnackBar(NOTIFY_TYPE.INFO, 'Seems like it is invalid comment.');
      setDeleteConfirm(false);
      return;
    }
    deleteComment({ variables: { externalId: deleteExternalId } })
      .then(() => refetchCommentHistory())
      .catch(() => {
        openSnackBar(NOTIFY_TYPE.ERROR, 'Something went wrong. Please try again.');
      });
    setDeleteConfirm(false);
  };

  const handleCancelDelete = (): void => {
    setDeleteConfirm(false);
  };

  return (
    <IconSettings iconPath="/icons">
      {loader || editCommentLoading || deleteCommentLoading ? (
        <Spinner size="large" variant="base" assistiveText={{ label: 'Loading...' }} />
      ) : (
        <div className="slds-m-around_small">
          <SnackBar open={open} notification={notification} />
          <Card
            id="Comments"
            heading={`Comments  ${commentsList.length > 0 ? `(${commentsList.length})` : ``}`}
            icon={<Icon category="standard" name="feedback" size="small" />}
          >
            <hr className="card-divider" />
            <div className="review-comments-container">
              <div className="comments-container">
                {commentsList.map((comment: Comment) => (
                  <div className="comment-body" key={comment.commentExternalId}>
                    <p className="comment-user-name">
                      {comment.userName || ''}{' '}
                      {userInfo?.id === comment.userId && (
                        <>
                          <Button
                            assistiveText={{ icon: 'Edit Comment' }}
                            iconCategory="utility"
                            iconName="edit"
                            iconSize="small"
                            iconVariant="bare"
                            onClick={(): void => {
                              setIsEdit(comment.commentExternalId);
                              setEditFieldText(comment.comment);
                            }}
                            variant="icon"
                          />
                          <Button
                            assistiveText={{ icon: 'Delete Comment' }}
                            iconCategory="utility"
                            iconName="delete"
                            iconSize="small"
                            iconVariant="bare"
                            onClick={(): void => {
                              setDeleteExternalId(comment.commentExternalId);
                              setDeleteConfirm(true);
                            }}
                            variant="icon"
                          />
                        </>
                      )}
                    </p>
                    <h4 className="comment-timestamp">
                      {`${comment.updatedBy ? 'Edited - ' : ''} ${moment(comment.postTime).format(
                        'L'
                      )} at ${moment(comment.postTime).format('LT')} `}
                    </h4>
                    {isEdit === comment.commentExternalId ? (
                      <>
                        <TextArea
                          label=""
                          tooltipText="Edit Comment"
                          showTooltip={false}
                          disabled={false}
                          handleChangeText={(text: string): void => setEditFieldText(text)}
                          value={editFieldText}
                          maxLength="200"
                        />
                        <Grid container justifyContent="flex-end">
                          <Grid item>
                            <Button align="right" label="Cancel" onClick={(): void => setIsEdit('')} />
                            <Button
                              align="right"
                              label="Save"
                              className="yellow-btn"
                              onClick={(): void => handleEdit(comment)}
                              disabled={editFieldText === comment.comment}
                            />
                          </Grid>
                        </Grid>
                      </>
                    ) : (
                      <p className="comments_text">{comment.comment}</p>
                    )}
                  </div>
                ))}
              </div>
              <TextArea
                label="Add Comment"
                tooltipText="Add Comment"
                showTooltip={false}
                disabled={false}
                handleChangeText={(text: string): void => setInput(text)}
                value={input}
                maxLength="200"
              />
              <Grid container justifyContent="flex-end">
                <Grid item>
                  <Button align="right" label="Add" onClick={handleAdd} />
                </Grid>
              </Grid>
            </div>
          </Card>
        </div>
      )}
      {deleteConfirm && (
        <Modal
          className="cancel-creation-modal default-modal_header default-modal_footer"
          isOpen={deleteConfirm}
          onRequestClose={handleCancelDelete}
          ariaHideApp={false}
          footer={[
            <Button label="Yes" onClick={handleDelete} key="yes" />,
            <Button label="No" variant="brand" onClick={handleCancelDelete} key="no" />,
          ]}
          heading="Delete Confirmation"
        >
          <div className="slds-text-align--center">
            <div>
              <h1 className="slds-m-around--large ">Are you sure to delete this comment?</h1>
            </div>
          </div>
        </Modal>
      )}
    </IconSettings>
  );
};

export default ExpenseInventoryComments;
